import { motion } from "framer-motion";
import { ArrowRight, Unlink } from "lucide-react";
import { Reveal, Overline } from "./Reveal";

const FRAGMENTS = ["A website", "Posted content", "Occasional ads", "Leads trickling in", "Manual follow-up", "Lost opportunities"];
const FLOW = ["Attention", "Traffic", "Lead", "Follow-Up", "Appointment", "Customer"];

export default function Problem() {
  return (
    <section id="problem" data-testid="problem-section" className="relative px-6 py-28 md:px-12 md:py-40">
      <div className="mx-auto max-w-[1600px]">
        <Reveal><Overline>01 — The Problem</Overline></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-8 max-w-4xl font-display text-3xl font-extrabold uppercase leading-[1.05] tracking-tight text-white md:text-5xl lg:text-6xl">
            More marketing isn&rsquo;t always the answer. <span className="text-zinc-500">A better system is.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-zinc-400 md:text-lg">
            Most businesses already have the pieces — they just don&rsquo;t connect. TPA connects
            those pieces into one customer acquisition system.
          </p>
        </Reveal>

        <div className="mt-20 grid gap-16 lg:grid-cols-2">
          <Reveal delay={0.1}>
            <p className="mb-8 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-zinc-500">
              <Unlink size={14} className="text-crimson" /> Disconnected pieces
            </p>
            <div className="flex flex-wrap gap-3">
              {FRAGMENTS.map((f, i) => (
                <motion.span
                  key={f}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08, duration: 0.5 }}
                  className="border border-dashed border-line px-4 py-2.5 text-sm text-zinc-500"
                >
                  {f}
                </motion.span>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <p className="mb-8 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-zinc-300">
              <span className="h-1.5 w-1.5 bg-crimson" /> One connected system
            </p>
            <div className="flex flex-wrap items-center gap-y-4">
              {FLOW.map((step, i) => (
                <span key={step} className="flex items-center">
                  <motion.span
                    initial={{ opacity: 0, scale: 0.85 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.12, duration: 0.5 }}
                    className="border border-white/25 bg-white/[0.03] px-4 py-2.5 font-display text-sm font-bold uppercase tracking-wide text-white"
                  >
                    {step}
                  </motion.span>
                  {i < FLOW.length - 1 && (
                    <motion.span
                      initial={{ opacity: 0, x: -8 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.12 + 0.15, duration: 0.4 }}
                      className="px-2 text-crimson"
                    >
                      <ArrowRight size={16} />
                    </motion.span>
                  )}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
